import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReducedMotion } from "../../lib/hooks";
import { BOOT, CLEAR, createCommands } from "../../lib/shell";

const MAX_LINES = 60;

/**
 * A working shell. It types the intro out on its own the first time it's on
 * screen, then hands the prompt over — commands, history, tab completion.
 *
 * State is a bounded array of printed lines; nothing here measures layout or
 * runs an animation loop.
 */
const Terminal = ({ onToggleTheme }) => {
  const [lines, setLines] = useState([]);
  const [booted, setBooted] = useState(false);
  const [input, setInput] = useState("");
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const navigate = useNavigate();
  const reduced = useReducedMotion();
  const rootRef = useRef(null);
  const bodyRef = useRef(null);
  const inputRef = useRef(null);

  const print = useCallback((entries) => {
    setLines((prev) => [...prev, ...entries].slice(-MAX_LINES));
  }, []);

  const commands = useMemo(
    () => createCommands({ navigate, toggleTheme: onToggleTheme }),
    [navigate, onToggleTheme]
  );

  // Type the intro out once the panel is actually visible.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;

    if (reduced || typeof IntersectionObserver === "undefined") {
      setLines(BOOT.flatMap((l) => [{ kind: "cmd", text: l.cmd }, { kind: "out", node: l.out }]));
      setBooted(true);
      return undefined;
    }

    let timer;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();

        const step = (i) => {
          if (i >= BOOT.length) {
            setBooted(true);
            return;
          }
          setLines((prev) => [
            ...prev,
            { kind: "cmd", text: BOOT[i].cmd },
            { kind: "out", node: BOOT[i].out },
          ]);
          timer = setTimeout(() => step(i + 1), 460);
        };
        timer = setTimeout(() => step(0), 400);
      },
      { threshold: 0.25 }
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, [reduced]);

  // Keep the newest line in view without moving the page.
  useEffect(() => {
    const body = bodyRef.current;
    if (body) body.scrollTop = body.scrollHeight;
  }, [lines, booted]);

  const run = useCallback(
    (raw) => {
      const trimmed = raw.trim();
      const entries = [{ kind: "cmd", text: trimmed || " " }];

      if (trimmed) {
        const [name, ...args] = trimmed.split(/\s+/);
        const command = commands[name.toLowerCase()];
        if (command) {
          const output = command(args, commands);
          if (output === CLEAR) {
            setLines([]);
            setHistory((h) => [...h, trimmed].slice(-40));
            setHistoryIndex(-1);
            return;
          }
          if (output !== null) entries.push({ kind: "out", node: output });
        } else {
          entries.push({
            kind: "err",
            node: (
              <>
                command not found: {name} — try <u>help</u>
              </>
            ),
          });
        }
        setHistory((h) => [...h, trimmed].slice(-40));
      }

      setHistoryIndex(-1);
      print(entries);
    },
    [commands, print]
  );

  const onKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      run(input);
      setInput("");
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!history.length) return;
      const next = historyIndex === -1 ? history.length - 1 : Math.max(0, historyIndex - 1);
      setHistoryIndex(next);
      setInput(history[next]);
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (historyIndex === -1) return;
      const next = historyIndex + 1;
      if (next >= history.length) {
        setHistoryIndex(-1);
        setInput("");
      } else {
        setHistoryIndex(next);
        setInput(history[next]);
      }
      return;
    }

    if (event.key === "Tab") {
      event.preventDefault();
      const partial = input.trim().toLowerCase();
      if (!partial) return;
      const match = Object.keys(commands)
        .sort()
        .find((name) => name.startsWith(partial));
      if (match) setInput(match);
      return;
    }

    // Ctrl-L clears, same as the real thing.
    if (event.key.toLowerCase() === "l" && event.ctrlKey) {
      event.preventDefault();
      setLines([]);
    }
  };

  return (
    <div
      className="terminal"
      ref={rootRef}
      onClick={() => inputRef.current?.focus()}
      role="presentation"
    >
      <div className="terminal__bar">
        <i /><i /><i />
        <span>ouri@portfolio — zsh</span>
      </div>

      <div className="terminal__body" ref={bodyRef}>
        {lines.map((line, i) =>
          line.kind === "cmd" ? (
            <p className="terminal__line" key={i}>
              <b>➜</b>
              <span>{line.text}</span>
            </p>
          ) : (
            <p
              className={`terminal__line terminal__line--out${
                line.kind === "err" ? " terminal__line--err" : ""
              }`}
              key={i}
            >
              <span>{line.node}</span>
            </p>
          )
        )}

        {booted ? (
          <label className="terminal__prompt">
            <span className="sr-only">Terminal input — type a command</span>
            <b aria-hidden="true">➜</b>
            <input
              ref={inputRef}
              className="terminal__input"
              /* Monospace, so 1ch per character sizes the field to its text
                 exactly — which puts the block caret right after the cursor. */
              style={{ width: `${Math.max(1, input.length)}ch` }}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              spellCheck="false"
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              aria-label="Terminal input"
              enterKeyHint="go"
            />
            <i className="caret" aria-hidden="true" />
          </label>
        ) : (
          <p className="terminal__line">
            <b>➜</b>
            <i className="caret" aria-hidden="true" />
          </p>
        )}
      </div>

      {booted && (
        <div className="terminal__hint" aria-hidden="true">
          <span>type <kbd>help</kbd></span>
          <span><kbd>↑</kbd> history</span>
          <span><kbd>tab</kbd> complete</span>
        </div>
      )}
    </div>
  );
};

export default Terminal;
